import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../../components/common/Toast/toast-type'
import { theme } from '../../../../../../theme/theme'
import {
  Button,
  ButtonVariant,
} from '../../../../../../components/common/Button'
import { Icon, IconChoices } from '../../../../../../components/common/Icon'
import { managementBinderUploadSchema } from '../../../../../../forms/schemas/management_binder_upload'
import {
  DropZone,
  DropZoneVariant,
} from '../../../../../../components/common/DropZone'
import { binderQueries } from '../../../../../../api/binder'
import { useCurrentSociety } from '../../../../../../hooks/useCurrentSociety'
import { TextTooltip } from '../../../../../../components/common/Tooltip'
import { unitQueries } from '../../../../../../api/units'
import { entranceQueries } from '../../../../../../api/society-entrances'
import { unitString } from '../../../../../../hooks/useUnitsString'
import { SelectDropdown } from '../../../../../../components/common/SelectDropdown'
import { FormControl } from '../../../../../../components/common/FormControl'
import { i18nSocietyType } from '../../../../../../i18n'
import { NOTIFICATION_SETTINGS_VALUE } from '../../../../../../helpers/notification_settings_options'
import { useFormErrorMessage } from '../../../../../../hooks/useFormErrorMessage'

interface TreeNode {
  name: string
  isFolder: boolean
  children: TreeNode[]
  fileIndex?: number
  path: string
  collides?: string[]
}

// Build a tree structure from flat file list with paths
const buildFileTree = (
  files: { name: string; path?: string }[],
  filePathCollides?: {
    unitId: string
    filePaths: string[]
  }[]
): TreeNode[] => {
  const root: TreeNode[] = []

  files.forEach((file, index) => {
    const pathParts = (file.path || file.name).split('/')
    let currentLevel = root

    pathParts.forEach((part, partIndex) => {
      const isLastPart = partIndex === pathParts.length - 1
      const existingNode = currentLevel.find((node) => node.name === part)

      if (existingNode) {
        if (isLastPart && !existingNode.isFolder) {
          // This is a duplicate file, skip
          return
        }
        currentLevel = existingNode.children
      } else {
        const collidesPerUnit =
          filePathCollides?.map((unit) => unit.unitId) ?? []
        const colliding = filePathCollides?.some((unit) => {
          // For thumbnail and descriptions, ignore extension
          if (file.path) {
            const base = file.path.replace(/\.[^/.]+$/, '')
            if (
              base.includes('folderDescription') ||
              base.includes('folderThumbnail')
            ) {
              if (
                unit.filePaths.some((p) => p.replace(/\.[^/.]+$/, '') === base)
              ) {
                return true
              }
            }
          }
          // For normal objects
          return unit.filePaths.includes(file.path!)
        })
        const collides = isLastPart && colliding ? collidesPerUnit : []
        const newNode: TreeNode = {
          name: part,
          isFolder: !isLastPart,
          children: [],
          fileIndex: isLastPart ? index : undefined,
          path: pathParts.slice(0, partIndex + 1).join('/'),
          collides,
        }
        currentLevel.push(newNode)
        currentLevel = newNode.children
      }
    })
  })

  // Recursively sort nodes so folderDescription and folderThumbnail are always first in their folders
  const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes
      .sort((a, b) => {
        const aSpecial =
          a.name.includes('folderDescription') ||
          a.name.includes('folderThumbnail')
        const bSpecial =
          b.name.includes('folderDescription') ||
          b.name.includes('folderThumbnail')
        if (aSpecial && !bSpecial) return -1
        if (!aSpecial && bSpecial) return 1
        if (aSpecial && bSpecial) {
          return a.name.localeCompare(b.name)
        }

        if (a.isFolder && !b.isFolder) return 1
        if (!a.isFolder && b.isFolder) return -1

        return a.name.localeCompare(b.name)
      })
      .map((node) =>
        node.isFolder ? { ...node, children: sortNodes(node.children) } : node
      )
  }

  return sortNodes(root)
}

interface TreeCount {
  folders: number
  files: number
}

const countTreeNodes = (nodes: TreeNode[]): TreeCount => {
  let folders = 0
  let files = 0

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const traverse = (node: TreeNode) => {
    if (node.isFolder) {
      folders += 1
      node.children.forEach(traverse)
    } else {
      files += 1
    }
  }

  nodes.forEach(traverse)

  return { folders, files }
}

interface FileTreeNodeProps {
  node: TreeNode
  depth: number
  onRemove: (index: number) => void
}

const FileTreeNode = ({
  node,
  depth,
  onRemove,
}: FileTreeNodeProps): JSX.Element => {
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)
  const { translate } = useAppTranslation()
  const nonUploadedFilesStyles =
    node.name.includes('folderDescription') ||
    node.name.includes('folderThumbnail')
      ? 'opacity-60 gray-400 italic'
      : ''
  const { society } = useCurrentSociety()
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id || '')
  )
  const { data: societyEntrences } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )

  const unitStrings = societyUnits
    ?.filter((unit) => node.collides && node.collides.includes(unit._id))
    .map((unit) =>
      unitString([unit], society ? [society] : [], societyEntrences || [], true)
    )

  return (
    <div>
      <div
        className={`flex items-center gap-2 rounded py-1 hover:bg-gray-50 `}
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        <Icon
          icon={node.isFolder ? IconChoices.FOLDER : IconChoices.DOCUMENT}
          size={18}
          className={nonUploadedFilesStyles}
        />
        <span
          style={theme.textVariants.base}
          className={`flex-1 ${node.isFolder ? 'font-medium' : ''} ${nonUploadedFilesStyles}`}
        >
          {node.name}
        </span>

        {node.collides && node.collides.length > 0 && (
          <TextTooltip
            title={translate('binderPickerView.tooltip.title')}
            className="z-1"
            referenceElement={referenceElement}
            useFlexibleWidth
            text={translate('binderPickerView.tooltip.text', {
              count: unitStrings?.length || 0,
              units: unitStrings?.join(', '),
            })}
          >
            <Icon
              icon={IconChoices.REPORT}
              color={theme.colors.warning}
              size={20}
              ref={setReferenceElement}
            />
          </TextTooltip>
        )}

        {!node.isFolder && node.fileIndex !== undefined && (
          <Icon
            icon={IconChoices.DELETE_TRASH}
            size={20}
            color={theme.colors.danger}
            onClick={() => onRemove(node.fileIndex as number)}
          />
        )}
      </div>
      {node.children.map((child) => (
        <FileTreeNode
          key={child.path}
          node={child}
          depth={depth + 1}
          onRemove={onRemove}
        />
      ))}
    </div>
  )
}

interface UploadBinderFormProps {
  onError: (error: unknown) => void
  onSubmit: (
    data: InferType<typeof managementBinderUploadSchema> & {
      rejectedFiles?: { filename: string; error: string }[]
    }
  ) => void
  onClose: () => void
  loading: boolean
  unitIds: string[]
  folderId?: string
  optionSettings: {
    value: 'none' | 'notification'
    label: string
  }[]
  published?: boolean
}

export const UploadBinderForm = ({
  loading,
  onError,
  onSubmit,
  onClose,
  unitIds,
  folderId,
  optionSettings,
  published,
}: UploadBinderFormProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()
  const { getErrorMessage } = useFormErrorMessage()
  const [allFiles, setAllFiles] = useState<
    {
      path: string
      mimeType: string
      base64: string
      filename: string
      name: string
      id: string
    }[]
  >([])
  const [invalidFiles, setInvalidFiles] = useState<
    { filename: string; error: string }[]
  >([])

  const rejectionReason = useCallback(
    (errorCode?: string): string => {
      switch (errorCode) {
        case 'file-invalid-type':
          return translate('common.form.upload.invalidType')
        case 'file-too-large':
          return translate('common.form.upload.fileTooLarge')
        default:
          return translate('common.form.upload.genericError')
      }
    },
    [translate]
  )

  const { data: filePathCollides, refetch: refetchFilePathCollides } = useQuery(
    binderQueries.checkFileCollisions(
      allFiles.map((file) => file.path),
      unitIds,
      society?._id || '',
      folderId
    )
  )

  const getDefaultValues = useCallback((): InferType<
    typeof managementBinderUploadSchema
  > => {
    return {
      files: [],
      notificationSettings: NOTIFICATION_SETTINGS_VALUE.NONE,
    }
  }, [])

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(managementBinderUploadSchema),
    defaultValues: getDefaultValues(),
  })

  const { fields, append, remove } = useFieldArray({
    name: 'files',
    control,
  })

  // For some reason browers dont like .md files (When using drag and drop)
  // and dont provide the correct mime type, so we need to manually set it
  const getMimeType = (filename: string, browserMimeType: string): string => {
    if (browserMimeType) return browserMimeType
    const ext = filename.split('.').pop()?.toLowerCase()
    const mimeTypes: Record<string, string> = {
      md: 'text/markdown',
    }
    return mimeTypes[ext || ''] || 'application/octet-stream'
  }

  useEffect(() => {
    const files = fields.map((file) => {
      return {
        ...file,
        path: file.path ? file.path : file.name,
      }
    })
    setAllFiles(files)
  }, [fields])

  const fileTree = useMemo(
    () => buildFileTree(allFiles, filePathCollides || []),
    [allFiles, filePathCollides]
  )

  const { folders, files } = useMemo(() => countTreeNodes(fileTree), [fileTree])

  useEffect(() => {
    refetchFilePathCollides()
  }, [allFiles, refetchFilePathCollides, unitIds])

  useEffect(() => {
    if (allFiles.length === 0) {
      setInvalidFiles([])
    }
  }, [allFiles.length])

  return (
    <>
      {allFiles.length === 0 && (
        <Controller
          control={control}
          render={() => (
            <DropZone
              onUpload={(status, file, errorCode) => {
                if (status === 'accepted') {
                  append({
                    mimeType: getMimeType(file.name, file.type),
                    base64: file.base64,
                    filename: file.name,
                    name: file.name,
                    path: file.path || file.name,
                  })
                } else {
                  setInvalidFiles((prev) => [
                    ...prev,
                    {
                      filename: file.name,
                      error: rejectionReason(errorCode),
                    },
                  ])
                }
              }}
              onAllFilesRejected={() =>
                setToastNotification(
                  ToastType.DANGER,
                  translate('flashMessage.somethingWentWrongError')
                )
              }
              multiple
              variant={DropZoneVariant.FOLDER_AND_DOCUMENTS}
              allowCadFiles
            />
          )}
          name="files"
        />
      )}
      <div className="flex flex-col">
        {fields.length > 0 && (
          <p style={theme.textVariants.base} className="mb-2 mt-4">
            {translate('createUpdateBinderForm.filesToBeUploaded', {
              numberOfFiles: files + folders,
            })}
          </p>
        )}
        {fileTree.length > 0 && (
          <>
            <div className="max-h-96 overflow-y-auto rounded border border-gray-200 bg-white p-2">
              {fileTree.map((node) => (
                <FileTreeNode
                  key={node.path}
                  node={node}
                  depth={0}
                  onRemove={remove}
                />
              ))}
            </div>
            {published && (
              <Controller
                control={control}
                name="notificationSettings"
                render={({ field: { onChange, value, name } }) => (
                  <FormControl
                    label={translate('common.notificationSettings.label', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    })}
                    labelStyle={theme.textVariants.base}
                    name={name}
                    error={
                      errors.notificationSettings &&
                      getErrorMessage(errors.notificationSettings)
                    }
                  >
                    <SelectDropdown
                      options={optionSettings}
                      error={!!errors.notificationSettings}
                      value={optionSettings.find(
                        (option) => option.value === value
                      )}
                      onChange={(option) => onChange(option?.value)}
                    />
                  </FormControl>
                )}
              />
            )}
          </>
        )}
      </div>
      <div className="mt-4 flex flex-shrink-0 flex-wrap items-center justify-end gap-4">
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          variant={ButtonVariant.PRIMARY}
          loading={loading}
          disabled={!isValid || loading}
          label={translate('common.actions.upload')}
          type="submit"
          onClick={handleSubmit(
            (values) => onSubmit({ ...values, rejectedFiles: invalidFiles }),
            onError
          )}
        />
      </div>
    </>
  )
}
